/**
 * The draft/refined cycle, as the web app sees it.
 *
 * The STATE contract itself is shared with the server (`@shared-lib/constants/
 * depthFinalization`) — this adds only the user-facing copy, which lives in one
 * place so the header badge and the edit warning cannot describe the same
 * situation differently.
 */
import {
  DEPTH_STATE,
  DEPTH_SKIP_REASON,
  isDepthDraft,
  isDepthNoChange,
  refinedElsewhereId,
} from '@shared-lib/constants/depthFinalization';
import { SCAN_STAGE } from './scanStages';

export {
  DEPTH_STATE,
  DEPTH_SKIP_REASON,
  isDepthDraft,
  isDepthNoChange,
  refinedElsewhereId,
};

/**
 * Is this viewer's structure a draft?
 *
 * Reads both shapes `structureData` arrives in: most viewers get the assembled
 * doc with its fields at the top level, while ElevationsViewer receives one
 * wrapped in a `structure` key. That inconsistency predates this and is worked
 * around here ONCE rather than with an `||` at each of the four badge sites —
 * it is the same field either way, and the right long-term fix is to settle on
 * one shape.
 *
 * @param {Object} [structureData]
 * @returns {boolean}
 */
export function draftFromStructure(structureData) {
  const finalization =
    structureData?.depthFinalization ??
    structureData?.structure?.depthFinalization;
  return isDepthDraft(finalization);
}

/**
 * Deep content equality for finalization values: null/primitives, plain
 * objects, and arrays. Key order does not matter. Small by design — the field
 * is a handful of scalar keys, not arbitrary data.
 */
function sameFinalization(a, b) {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => sameFinalization(a[k], b[k]));
}

/**
 * Fold a live `depthFinalization` snapshot value onto a one-shot-loaded
 * structure, PRESERVING OBJECT IDENTITY when nothing changed.
 *
 * Identity is the contract here, not a micro-optimization: the structure this
 * mirrors onto is StructureViewerWrapper's `structureData` prop, and its
 * process-structure effect re-derives the whole viewer `data` — with
 * `data.rooms` falling back to the prop's page-load rooms — whenever the prop
 * identity changes without a roomsData change. An unconditional
 * `{ ...prev, depthFinalization }` therefore turned EVERY structure-doc write
 * into a revert of on-screen room edits: the vertex Delete/Move/Add tools
 * write structures/{id} directly, their own write echoed back through the
 * badge listener, and the just-edited polygon snapped back until reload.
 *
 * @param {Object|null} structure  the loaded structure (or null before load)
 * @param {Object|null} finalization  normalized snapshot value (missing → null)
 * @returns {Object|null} same reference when content-equal, else a new
 *   structure with the field set
 */
export function mirrorDepthFinalization(structure, finalization) {
  if (!structure) return structure;
  if (sameFinalization(structure.depthFinalization ?? null, finalization)) {
    return structure;
  }
  return { ...structure, depthFinalization: finalization };
}

/**
 * Why a scan will not be refined, in the words both the Process Status panel
 * and the reprocess message use.
 */
export const DEPTH_SKIP_DETAIL = {
  [DEPTH_SKIP_REASON.NO_LIDAR]: 'No LiDAR to measure against.',
  [DEPTH_SKIP_REASON.NO_TARGETS]: 'Nothing a measurement could settle.',
  [DEPTH_SKIP_REASON.EDITED]: 'This scan was edited, so it was left alone.',
  [DEPTH_SKIP_REASON.FAILED]: 'The refinement pass kept failing.',
  [DEPTH_SKIP_REASON.NOT_APPLIED]: `Rebuilt without its LiDAR measurements — reprocess through ${SCAN_STAGE['refined-structure']} to apply them.`,
  [DEPTH_SKIP_REASON.UNAVAILABLE]:
    'LiDAR refinement is not set up in this environment.',
};

/** The skip detail for a reason, with a fallback for one this copy predates. */
export const depthSkipDetail = (skipReason) =>
  DEPTH_SKIP_DETAIL[skipReason] || 'Will not be refined.';

/**
 * What the ⓘ beside the Draft badge says, and what the notice under the card
 * repeats at length.
 *
 * Note what this no longer says: editing used to CANCEL the refinement, and the
 * copy said so. It now moves it — the refined version is saved as a separate
 * scan — so the old wording described a behaviour that no longer exists, in the
 * one place a user would rely on it.
 *
 * The clock is anchored to the UPLOADS, not to now. The refinement measures
 * against the raw LiDAR sidecars, so it cannot start until the phone has
 * finished sending them — and the phone uploads one room at a time, serially,
 * over whatever connection it has. "About ten minutes" was counted from the
 * moment the structure appeared, which is the one moment the uploads are least
 * likely to be done: a scan whose sidecars were still going up read as overdue
 * within minutes of a promise nothing had started keeping.
 */
export const DRAFT_EXPLANATION =
  'This is a draft. The refined version will be ready 15-30 minutes after your ' +
  'uploads finish, and replaces this one automatically. If you edit before ' +
  'then, your edits are kept here and the refined version arrives as a ' +
  'separate scan in this project. Processing Status shows how far along it is.';

/**
 * The fuller version, shown under the name/badge card. Same substance as the
 * tooltip, deliberately — the two must never tell different stories about what
 * editing costs, or about when the refined version lands.
 *
 * Where the tooltip only NAMES Process Status, the notice can link to it: a
 * `title` attribute is plain text, the notice is real DOM. See
 * PROCESS_STATUS_LINK_LABEL.
 */
export const DRAFT_NOTICE =
  'This scan is still a draft. The refined version will be ready 15-30 ' +
  'minutes after your video and LiDAR uploads finish — it replaces this one ' +
  'automatically, and you do not need to do anything. If you edit it before ' +
  'then, your edits stay here and the refined version is saved as a separate ' +
  'scan in this project, so nothing is lost either way.';

/**
 * The notice's link into the Process Status panel, which is the only place that
 * answers the question the new copy raises — "so when DO my uploads finish?".
 *
 * A label rather than a route: the panel is viewer state, not a page, and the
 * host wires the action. Hosts with no panel to open (the standalone floor-plan
 * and elevations pages) pass no handler and the notice renders without it.
 */
export const PROCESS_STATUS_LINK_LABEL = 'See upload and processing status';

/**
 * The same destination as a BUTTON, on the reprocess toast.
 *
 * Short on purpose, and not a duplicate of the label above: the notice's link
 * sits INSIDE the sentence and wraps with it, where a full phrase reads as prose,
 * while the toast's action is a `white-space: nowrap` button beside an already
 * long caveat on a bubble with no max width. The full phrase there stretches the
 * toast across the screen.
 */
export const PROCESS_STATUS_ACTION_LABEL = 'See status';

/**
 * The draft caveat folded into the message a finished reprocess shows, so "it
 * worked" and "here is what happens next" arrive together instead of as two
 * notifications seconds apart saying overlapping things.
 */
export const draftReprocessSuccess = (base) => `${base} ${DRAFT_NOTICE}`;

/** Said when a run asked for refinement and the server queued none. */
export const REFINEMENT_NOT_QUEUED_NOTICE =
  'The LiDAR refinement could not be queued, so no refined version will follow.';

/** Said when the server did not report what it decided (it predates that). */
export const REFINEMENT_UNREPORTED_NOTICE =
  'Processing Status shows whether a refined version is on its way.';

/**
 * The message a finished reprocess shows, from what the SERVER decided.
 *
 * It used to promise a refined version whenever the button asked for one,
 * while a server with no measurement endpoint queued nothing: the message said
 * "ready in 15-30 minutes" and Process Status said "Not started". Now the
 * promise is made only when the run's own stamp is a draft.
 *
 * @param {string} base - "Scan reprocessed successfully!" and the like
 * @param {Object} args
 * @param {boolean} [args.requested] - the variant asked for refinement
 * @param {Object|null|undefined} [args.decision] - `processStructure`'s returned
 *   `depthFinalization`: the stamp, null when it wrote none, undefined from a
 *   server that does not say
 * @returns {{ message: string, linkToStatus: boolean, deliveredDraftNotice: boolean }}
 *   `linkToStatus` adds the "See status" action; `deliveredDraftNotice` means
 *   the under-card draft notice need not repeat it
 */
export function reprocessSuccessNotice(
  base,
  { requested = false, decision } = {}
) {
  if (isDepthDraft(decision)) {
    return {
      message: draftReprocessSuccess(base),
      linkToStatus: true,
      deliveredDraftNotice: true,
    };
  }
  if (decision?.state === DEPTH_STATE.SKIPPED) {
    return {
      message: `${base} No refined version will follow. ${depthSkipDetail(decision.skipReason)}`,
      linkToStatus: true,
      deliveredDraftNotice: false,
    };
  }
  if (!requested) {
    return { message: base, linkToStatus: false, deliveredDraftNotice: false };
  }
  return {
    message: `${base} ${
      decision === undefined
        ? REFINEMENT_UNREPORTED_NOTICE
        : REFINEMENT_NOT_QUEUED_NOTICE
    }`,
    linkToStatus: true,
    deliveredDraftNotice: false,
  };
}

/** Where the "already told them" mark for one scan lives. */
export const draftNoticeStorageKey = (structureId) =>
  `draft-notice-dismissed:${structureId}`;

/**
 * Announces the mark to cards that are ALREADY on screen.
 *
 * The storage key alone is not enough: the reprocess runs from inside the very
 * viewer the notice hangs under, so `BreadcrumbTitle` has long since mounted
 * and read the key. Without this event the caveat lands twice — once in the
 * success toast, once under the card — which is exactly what the fold was for.
 */
export const DRAFT_NOTICE_SEEN_EVENT = 'draft-notice-seen';

/**
 * Mark the under-card notice as already seen for a scan.
 *
 * Called when the reprocess success message has already carried the draft
 * caveat, so the same text does not appear twice seconds apart. The ⓘ still
 * re-opens it, so nothing is lost — it is suppressed, not withheld.
 */
export function markDraftNoticeSeen(structureId) {
  if (!structureId) return;
  try {
    sessionStorage.setItem(draftNoticeStorageKey(structureId), '1');
  } catch {
    // Private mode / storage disabled: the notice shows again, which is a far
    // better failure than never showing it.
  }
  // For the card that is already rendered — see DRAFT_NOTICE_SEEN_EVENT.
  window.dispatchEvent(
    new CustomEvent(DRAFT_NOTICE_SEEN_EVENT, { detail: { structureId } })
  );
}

/**
 * Told when the refinement was written to a SEPARATE scan because this one had
 * been edited. The link is the point: a second scan appearing in the project
 * list is easy to miss and easy to mistake for clutter.
 */
export const REFINED_ELSEWHERE_NOTICE =
  'You edited this scan, so the refined version was saved as a separate scan ' +
  'in this project. Your edits here are untouched.';

/** Told when the measurement ran and agreed with the pipeline. */
export const NO_CHANGES_NOTICE =
  'The refinement pass finished and found nothing to change — this scan is ' +
  'already accurate.';
